import {
  Box,
  type ColumnsType,
  CountAmountCell,
  CreateButton,
  Divider,
  Flex,
  MoreButton,
  Segmented,
  Space,
  Table,
  Text,
} from '@/components';
import { ProjectResourceGuard } from '@/features/authentication/_components/ProjectResourceGuard';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { useProjectPermissionAndAccessControl } from '@/features/authentication/_hooks/permission';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { useProject } from '@/features/project/_hooks/project';
import { useSubProjectsForKS } from '@/features/project/_hooks/sub-project';
import { downloadFundOwnerForLocalGovernmentReportCsv } from '@/features/project/projects/detail/execution-progress/_utils/download-fund-owner-for-local-government-report-csv';
import { useId } from '@/hooks';
import { useLocaledAmount, useMessage } from '@/providers';
import { EMPTY_DATA_ERROR, logger } from '@/utils';
import { AmountUnitLabelModifier, TABLE_BUTTON_WIDTH, isPS } from '@/utils';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { z } from 'zod';
import {
  ExecutionProgressTypeLabelModifier,
  executionProgressType,
} from '../../../_models/execution-progress';
import type { SubProjectForKSModel } from '../../../_models/sub-project';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { ExecutionProgressIndividualTableActions } from './ExecutionProgressIndividualTableActions';

type KeyType = 'pdf-preview' | 'csv-download';

type SubProjectForKs = z.infer<typeof SubProjectForKSModel>;
type SubProjectForKsColumns = ColumnsType<SubProjectForKs>;

type Props = {
  individualColumns: ColumnsType<SubProjectForKs>;
  fiscalYear: number;
  previousFiscalYear: number;
  projectName: string;
};

const PINPOINT_SHEET_TYPE = 'KS_LOCAL_GOVERNMENT';

export const IndividualTable: FC<Props> = ({
  individualColumns,
  fiscalYear,
  previousFiscalYear,
  projectName,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { unit } = useLocaledAmount();
  const { setDrawerState, drawerState } = useProjectDetailDrawerState();
  const { id: projectId } = useId();
  const project = useProject(projectId);

  const subProjects = useSubProjectsForKS(projectId);
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;
  const {
    hasAccessControlByOrganization,
    editPermission: hasProjectEditPermission,
  } = useProjectPermissionAndAccessControl(projectId);

  const editable = !project.data?.isLocked && hasProjectEditPermission;

  const [individualTableType, setIndividualTableType] = useState<
    z.infer<typeof executionProgressType>
  >('income-and-expenditure');

  const fundOwnerForLocalGovernmentReportPath = `${location.pathname}/fund-owner-for-local-government-report`;

  const fundBusinessColumns = useMemo(
    () => [
      {
        key: 'decidedFundBusinessForLocalGovernment',
        title: t('decision-on-project-implementation-with-year', {
          year: previousFiscalYear,
        }),
        children: [
          {
            key: 'decidedNumberOfSubsidy',
            title: t('fund-business-subsidy'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={fundBusinessForLocalGovernment.decidedNumberOfSubsidy}
                  amount={fundBusinessForLocalGovernment.decidedAmountOfSubsidy}
                  unit={unit}
                />
              ),
          },
          {
            key: 'decidedNumberOfLoan',
            title: t('investment'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={
                    fundBusinessForLocalGovernment.decidedNumberOfInvestment
                  }
                  amount={
                    fundBusinessForLocalGovernment.decidedAmountOfInvestment
                  }
                  unit={unit}
                />
              ),
          },
          {
            key: 'decidedNumberOfLoan',
            title: t('loan'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={fundBusinessForLocalGovernment.decidedNumberOfLoan}
                  amount={fundBusinessForLocalGovernment.decidedAmountOfLoan}
                  unit={unit}
                />
              ),
          },
          {
            key: 'decidedNumberOfDebtGuarantee',
            title: t('debt-guarantee'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={
                    fundBusinessForLocalGovernment.decidedNumberOfDebtGuarantee
                  }
                  amount={
                    fundBusinessForLocalGovernment.decidedAmountOfDebtGuarantee
                  }
                  unit={unit}
                />
              ),
          },
          {
            key: 'decidedNumberOfOthers',
            title: t('fund-business-for-local-government-others'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={fundBusinessForLocalGovernment.decidedNumberOfOthers}
                  amount={fundBusinessForLocalGovernment.decidedAmountOfOthers}
                  unit={unit}
                />
              ),
          },
        ],
      },
      {
        key: 'balancedFundBusinessForLocalGovernment',
        title: t('fund-business-loan-balance-title-with-year', {
          year: previousFiscalYear,
        }),
        children: [
          {
            key: 'balanceNumberOfSubsidy',
            title: t('investment'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={
                    fundBusinessForLocalGovernment.balanceNumberOfInvestment
                  }
                  amount={
                    fundBusinessForLocalGovernment.balanceAmountOfInvestment
                  }
                  unit={unit}
                />
              ),
          },
          {
            key: 'balanceNumberOfLoan',
            title: t('loan'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={fundBusinessForLocalGovernment.balanceNumberOfLoan}
                  amount={fundBusinessForLocalGovernment.balanceAmountOfLoan}
                  unit={unit}
                />
              ),
          },
          {
            key: 'balanceNumberOfDebtGuarantee',
            title: t('debt-guarantee'),
            render: (_, { fundBusinessForLocalGovernment }) =>
              fundBusinessForLocalGovernment && (
                <CountAmountCell
                  count={
                    fundBusinessForLocalGovernment.balanceNumberOfDebtGuarantee
                  }
                  amount={
                    fundBusinessForLocalGovernment.balanceAmountOfDebtGuarantee
                  }
                  unit={unit}
                />
              ),
          },
        ],
      },
    ],
    [t, previousFiscalYear, unit],
  );

  const viewableMenu = [
    { key: 'pdf-preview', label: t('pdf-preview') },
    { key: 'csv-download', label: t('csv-download') },
  ];

  const onClickMenuItem = useCallback(
    async (_key: string) => {
      const key = _key as KeyType | 'create';
      switch (key) {
        case 'create':
          setDrawerState({
            open: true,
            editSubProjectId: undefined,
            type: 'execution-progress',
            formType: 'fund-info',
          });
          break;
        case 'pdf-preview':
          navigate(fundOwnerForLocalGovernmentReportPath);
          break;
        case 'csv-download':
          try {
            await downloadFundOwnerForLocalGovernmentReportCsv({
              id: projectId,
              fiscalYear,
              projectName,
              isDebugMode,
            });
          } catch (e) {
            if (e instanceof Error && e.message === EMPTY_DATA_ERROR) {
              await openMessage(
                'error',
                t('error-message-csv-export-empty-data'),
              );
              return;
            }
            await openMessage('error', t('error-message-csv-download'));
          }
          break;
        default: {
          const check: never = key;
          logger.error('Invalid key', check);
        }
      }
    },
    [
      fiscalYear,
      projectName,
      fundOwnerForLocalGovernmentReportPath,
      isDebugMode,
      projectId,
      openMessage,
      navigate,
      setDrawerState,
      t,
    ],
  );

  return (
    <Flex vertical gap={16}>
      <Flex align="center" justify="space-between">
        <Flex align="center">
          <Text
            variant="h5"
            text={t('individual-table-with-unit', {
              unit: AmountUnitLabelModifier[unit],
            })}
          />
          <Divider type="vertical" />
          <Text color="secondary" text={t('count-and-amount')} />
          <Box marginLeft={24}>
            <Segmented
              options={executionProgressType.options.map((value) => ({
                value,
                label: ExecutionProgressTypeLabelModifier[value],
              }))}
              onChange={(value) => {
                setIndividualTableType(executionProgressType.parse(value));
              }}
            />
          </Box>
        </Flex>
        <ProjectResourceGuard>
          {isPS() ? null : hasAccessControlByOrganization ? (
            <Space>
              {editable && (
                <CreateButton
                  onCreate={() => {
                    setDrawerState({
                      formType: 'fund-info',
                      open: true,
                      type: 'execution-progress',
                      editSubProjectId: undefined,
                    });
                  }}
                />
              )}
              <MoreButton
                items={[
                  ...(editable ? [{ key: 'create', label: t('create') }] : []),
                  ...viewableMenu,
                ]}
                onClick={async ({ key: _key }) => {
                  await onClickMenuItem(_key);
                }}
              />
            </Space>
          ) : (
            <MoreButton
              items={viewableMenu}
              onClick={async ({ key: _key }) => {
                await onClickMenuItem(_key);
              }}
            />
          )}
        </ProjectResourceGuard>
      </Flex>
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType: PINPOINT_SHEET_TYPE,
          ksLocalGovernment: {
            path: { project: projectId },
            field: 'ks.local-government.individual-table',
          },
        })}
      >
        <Table
          dataSource={subProjects.data}
          rowKey="id"
          bordered
          pagination={false}
          isRowHighlighted={(record) => {
            if (
              !drawerState.open ||
              drawerState.type !== 'execution-progress'
            ) {
              return false;
            }

            const editTargetId =
              drawerState.formType === 'fund-info'
                ? drawerState.editSubProjectId
                : drawerState.subProjectId;
            return record.id === editTargetId;
          }}
          columns={[
            {
              title: t('fund-owner-for-local-government'),
              fixed: 'left',
              dataIndex: 'localFundOwner',
              width: 100,
            },
            {
              title: t('fund-name'),
              fixed: 'left',
              dataIndex: 'name',
              width: 240,
            },
            {
              title: t('fund-overview-for-local-government'),
              dataIndex: 'localFundOverview',
              width: 480,
            },
            ...(individualTableType === 'income-and-expenditure'
              ? individualColumns
              : fundBusinessColumns),
            ...(editable
              ? ([
                  {
                    title: '',
                    key: 'action',
                    align: 'center',
                    width: TABLE_BUTTON_WIDTH,
                    fixed: 'right',
                    render: (_, record) => (
                      <PublishedProjectMutableGuard>
                        <ExecutionProgressIndividualTableActions
                          tableType={individualTableType}
                          subProjectId={record.id}
                        />
                      </PublishedProjectMutableGuard>
                    ),
                  },
                ] satisfies SubProjectForKsColumns)
              : []),
          ]}
        />
      </PinpointMarker>
    </Flex>
  );
};
